// src/data/spells.js
// Magic spell definitions for combat
// All spells use glyphs from Glyphbinding - spell level = highest required glyph level

/**
 * Spell elements
 */
export const SPELL_ELEMENTS = {
    FIRE: 'fire',
    ICE: 'ice',
    EARTH: 'earth',
    AIR: 'air'
};

/**
 * Spell effect types
 */
export const SPELL_EFFECTS = {
    DOT: 'dot',           // Damage over time (Fire)
    SLOW: 'slow',         // Reduce mob attack speed (Ice)
    DEFENSE: 'defense',   // Temporary defense buff (Earth)
    CRIT: 'crit'          // Crit chance boost (Air)
};

/**
 * All spells organized by element and tier
 *
 * Tier 1: Single elemental glyph - early game
 * Tier 2: Elemental + primal glyph - mid game
 * Tier 3: 2 elemental + cosmic/forbidden glyphs - end game
 *
 * Spell level = highest required glyph level (requires Glyphbinding investment)
 */
export const SPELLS = {
    // ============================================
    // FIRE SPELLS - Damage over Time (Pyre's Burning Gift)
    // ============================================
    EMBER_STRIKE: {
        id: 'ember_strike',
        name: 'Ember Strike',
        element: SPELL_ELEMENTS.FIRE,
        tier: 1,
        level: 14,
        baseDamage: 12,
        effect: {
            type: SPELL_EFFECTS.DOT,
            damage: 2,        // Damage per tick
            duration: 3000,   // Total duration in ms
            ticks: 3          // Number of damage ticks
        },
        glyphCost: [
            { glyphId: 'flame_glyph', quantity: 1 }
        ],
        color: '#FF4500',
        icon: '🔥',
        description: 'A bolt of flame that burns the target.'
    },
    PYRES_WRATH: {
        id: 'pyres_wrath',
        name: "Pyre's Wrath",
        element: SPELL_ELEMENTS.FIRE,
        tier: 2,
        level: 45,
        baseDamage: 28,
        effect: {
            type: SPELL_EFFECTS.DOT,
            damage: 5,
            duration: 4000,
            ticks: 4
        },
        glyphCost: [
            { glyphId: 'flame_glyph', quantity: 2 },
            { glyphId: 'entropy_glyph', quantity: 2 }
        ],
        color: '#FF6347',
        icon: '🔥',
        description: 'Channel the Pyre\'s chaotic flames to devastate your foe.'
    },
    VEILFIRE: {
        id: 'veilfire',
        name: 'Veilfire',
        element: SPELL_ELEMENTS.FIRE,
        tier: 3,
        level: 85,
        baseDamage: 52,
        effect: {
            type: SPELL_EFFECTS.DOT,
            damage: 10,
            duration: 5000,
            ticks: 5
        },
        glyphCost: [
            { glyphId: 'flame_glyph', quantity: 4 },
            { glyphId: 'entropy_glyph', quantity: 2 },
            { glyphId: 'sanguine_glyph', quantity: 2 }
        ],
        color: '#FF0000',
        icon: '🔥',
        description: 'Unleash blood-fueled flames from beyond the Veil.'
    },

    // ============================================
    // ICE SPELLS - Slow Effect (Cold Beyond the Veil)
    // ============================================
    FROSTBITE: {
        id: 'frostbite',
        name: 'Frostbite',
        element: SPELL_ELEMENTS.ICE,
        tier: 1,
        level: 5,
        baseDamage: 8,
        effect: {
            type: SPELL_EFFECTS.SLOW,
            multiplier: 0.85,   // 15% slow (mob attacks 15% slower)
            duration: 4000
        },
        glyphCost: [
            { glyphId: 'frost_glyph', quantity: 1 }
        ],
        color: '#87CEEB',
        icon: '❄️',
        description: 'A shard of frost that chills and slows the target.'
    },
    GLACIAL_GRASP: {
        id: 'glacial_grasp',
        name: 'Glacial Grasp',
        element: SPELL_ELEMENTS.ICE,
        tier: 2,
        level: 55,
        baseDamage: 24,
        effect: {
            type: SPELL_EFFECTS.SLOW,
            multiplier: 0.70,   // 30% slow
            duration: 5000
        },
        glyphCost: [
            { glyphId: 'frost_glyph', quantity: 2 },
            { glyphId: 'void_glyph', quantity: 2 }
        ],
        color: '#4169E1',
        icon: '❄️',
        description: 'The cold emptiness of the Void freezes your enemy.'
    },
    VOID_CHILL: {
        id: 'void_chill',
        name: 'Void Chill',
        element: SPELL_ELEMENTS.ICE,
        tier: 3,
        level: 75,
        baseDamage: 38,
        effect: {
            type: SPELL_EFFECTS.SLOW,
            multiplier: 0.50,   // 50% slow
            duration: 6000
        },
        glyphCost: [
            { glyphId: 'frost_glyph', quantity: 4 },
            { glyphId: 'void_glyph', quantity: 2 },
            { glyphId: 'doom_glyph', quantity: 2 }
        ],
        color: '#00CED1',
        icon: '❄️',
        description: 'Channel the deathly cold from beyond the Veil itself.'
    },

    // ============================================
    // EARTH SPELLS - Defense Buff (Mountain's Protection)
    // Low damage, high survivability
    // ============================================
    STONE_WARD: {
        id: 'stone_ward',
        name: 'Stone Ward',
        element: SPELL_ELEMENTS.EARTH,
        tier: 1,
        level: 9,
        baseDamage: 5,
        effect: {
            type: SPELL_EFFECTS.DEFENSE,
            bonus: 10,          // +10 defense
            duration: 8000
        },
        glyphCost: [
            { glyphId: 'stone_glyph', quantity: 1 }
        ],
        color: '#8B4513',
        icon: '🪨',
        description: 'Harden your skin with stone. Low damage, high defense.'
    },
    EARTHEN_SHELL: {
        id: 'earthen_shell',
        name: 'Earthen Shell',
        element: SPELL_ELEMENTS.EARTH,
        tier: 2,
        level: 25,
        baseDamage: 12,
        effect: {
            type: SPELL_EFFECTS.DEFENSE,
            bonus: 25,
            duration: 10000
        },
        glyphCost: [
            { glyphId: 'stone_glyph', quantity: 2 },
            { glyphId: 'vigor_glyph', quantity: 2 }
        ],
        color: '#A0522D',
        icon: '🪨',
        description: 'Fortify your body with earthen vigor. Low damage, high defense.'
    },
    MOUNTAINS_EMBRACE: {
        id: 'mountains_embrace',
        name: "Mountain's Embrace",
        element: SPELL_ELEMENTS.EARTH,
        tier: 3,
        level: 65,
        baseDamage: 18,
        effect: {
            type: SPELL_EFFECTS.DEFENSE,
            bonus: 50,
            duration: 12000
        },
        glyphCost: [
            { glyphId: 'stone_glyph', quantity: 4 },
            { glyphId: 'vigor_glyph', quantity: 2 },
            { glyphId: 'binding_glyph', quantity: 2 }
        ],
        color: '#696969',
        icon: '🪨',
        description: 'Channel the unyielding strength of mountains. Ultimate protection.'
    },

    // ============================================
    // AIR SPELLS - Critical Hit Boost (Storm's Fury)
    // ============================================
    GUST_STRIKE: {
        id: 'gust_strike',
        name: 'Gust Strike',
        element: SPELL_ELEMENTS.AIR,
        tier: 1,
        level: 1,
        baseDamage: 6,
        effect: {
            type: SPELL_EFFECTS.CRIT,
            bonus: 10,          // +10% crit chance (adds to base 5%)
            duration: 5000
        },
        glyphCost: [
            { glyphId: 'storm_glyph', quantity: 1 }
        ],
        color: '#87CEEB',
        icon: '💨',
        description: 'A swift strike of wind. Increases crit chance by 10%.'
    },
    LIGHTNING_ARC: {
        id: 'lightning_arc',
        name: 'Lightning Arc',
        element: SPELL_ELEMENTS.AIR,
        tier: 2,
        level: 35,
        baseDamage: 22,
        effect: {
            type: SPELL_EFFECTS.CRIT,
            bonus: 20,          // +20% crit chance
            duration: 6000
        },
        glyphCost: [
            { glyphId: 'storm_glyph', quantity: 2 },
            { glyphId: 'verdant_glyph', quantity: 2 }
        ],
        color: '#FFD700',
        icon: '⚡',
        description: 'Nature\'s lightning strikes with precision. +20% crit chance.'
    },
    TEMPESTS_FURY: {
        id: 'tempests_fury',
        name: "Tempest's Fury",
        element: SPELL_ELEMENTS.AIR,
        tier: 3,
        level: 95,
        baseDamage: 65,
        effect: {
            type: SPELL_EFFECTS.CRIT,
            bonus: 35,          // +35% crit chance
            duration: 8000
        },
        glyphCost: [
            { glyphId: 'storm_glyph', quantity: 4 },
            { glyphId: 'verdant_glyph', quantity: 2 },
            { glyphId: 'spirit_glyph', quantity: 2 }
        ],
        color: '#9932CC',
        icon: '⚡',
        description: 'Unleash the fury of spirits and storms. +35% crit chance.'
    }
};

/**
 * Get spell by ID
 * @param {string} spellId - Spell identifier
 * @returns {Object|null} Spell data or null if not found
 */
export function getSpell(spellId) {
    return Object.values(SPELLS).find(s => s.id === spellId) || null;
}

/**
 * Get all spells of a given tier
 * @param {number} tier - Spell tier (1-3)
 * @returns {Object[]} Array of spells
 */
export function getSpellsByTier(tier) {
    return Object.values(SPELLS).filter(s => s.tier === tier);
}

/**
 * Get all spells available at a given combat level
 * @param {number} combatLevel - Player's combat level
 * @returns {Object[]} Array of available spells sorted by level
 */
export function getAvailableSpells(combatLevel) {
    return Object.values(SPELLS)
        .filter(s => s.level <= combatLevel)
        .sort((a, b) => a.level - b.level);
}

/**
 * Get all spells (unsorted)
 * @returns {Object[]} Array of all spells
 */
export function getAllSpells() {
    return Object.values(SPELLS);
}

/**
 * Format spell effect for display
 * @param {Object} effect - Spell effect object
 * @returns {string} Human-readable effect description
 */
export function formatSpellEffect(effect) {
    switch (effect.type) {
        case SPELL_EFFECTS.DOT:
            return `+${effect.damage} dmg/tick (${effect.ticks} ticks, ${effect.duration / 1000}s)`;
        case SPELL_EFFECTS.SLOW:
            const slowPercent = Math.round((1 - effect.multiplier) * 100);
            return `${slowPercent}% slow for ${effect.duration / 1000}s`;
        case SPELL_EFFECTS.DEFENSE:
            return `+${effect.bonus} defense for ${effect.duration / 1000}s`;
        case SPELL_EFFECTS.CRIT:
            return `+${effect.bonus}% crit chance for ${effect.duration / 1000}s`;
        default:
            return 'Unknown effect';
    }
}

/**
 * Format glyph cost for display
 * @param {Array} glyphCost - Array of glyph cost objects
 * @returns {string} Formatted cost string
 */
export function formatGlyphCost(glyphCost) {
    return glyphCost.map(cost => {
        const glyphName = cost.glyphId.replace('_glyph', '').replace('_', ' ');
        const capitalizedName = glyphName.charAt(0).toUpperCase() + glyphName.slice(1);
        return `${cost.quantity}x ${capitalizedName}`;
    }).join(', ');
}

export default {
    SPELL_ELEMENTS,
    SPELL_EFFECTS,
    SPELLS,
    getSpell,
    getSpellsByTier,
    getAvailableSpells,
    getAllSpells,
    formatSpellEffect,
    formatGlyphCost
};
